// Office "Personal data requests" page (Session ASQ, Stream G, Item 2; ADMIN ONLY, office UI only). When somebody asks
// for a copy of the personal information ELE holds about them (a subject access request), the admin searches for them
// here by email address, phone number or name, confirms which records are theirs, and downloads one ZIP: a readable
// summary, every record in full, and the documents on file. The server does the finding (the personal data inventory
// decides where to look); this page shows what it found and keeps the admin's choice.
//
// WHAT IS TICKED FOR YOU, AND WHY. The server marks each record `preselected`: found by the person's own email address
// or phone number, or hanging off a record that is plainly theirs. A record found by NAME only, found only through
// someone else's details recorded on it (a next of kin, a witness, a second contact), a subcontractor (its payment
// details go with it), or linked to a customer account that may be an organisation, is left unticked with a line saying
// why: the admin ticks it only once they are sure. The server refuses to export anything its own search did not find. Nothing is ever ticked that the server did not tick, and nothing is exported that
// the admin did not leave ticked.
//
// THE SELECTION LIVES IN ONE PLACE (`selected`, keyed "Model:id"), with the identifiers that were searched
// (`searched`), so the second action panel builder B-ui adds below the results (erasure) reads exactly what the export
// reads. The marked spot for it is at the foot of the results.
import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import type { GdprIdentifiers, GdprMatchBasis, GdprPersonMatch, GdprRecordRef, GdprSearchResult } from "../../lib/api";
import { ErasurePanel, LiftErasureRecord } from "./gdpr/ErasurePanel";
import { EmptyState } from "../../components/EmptyState";
import { Button } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";

const BASIS_LABELS: Record<GdprMatchBasis, string> = {
  email: "Email address",
  phone: "Phone number",
  text: "Mentioned in the record",
  link: "Linked record",
  name: "Name only",
};

function keyOf(r: GdprRecordRef): string {
  return `${r.model}:${r.id}`;
}

// Several emails or numbers may be given in one box, separated by commas (a name is one value: "Smith, Jane" is a name).
function splitList(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function PersonalDataPage() {
  const [emailText, setEmailText] = useState("");
  const [phoneText, setPhoneText] = useState("");
  const [nameText, setNameText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // THE selection state: the result, the identifiers it was found by, and the records the admin has ticked.
  const [result, setResult] = useState<GdprSearchResult | null>(null);
  const [searched, setSearched] = useState<GdprIdentifiers | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exported, setExported] = useState(false);

  const canSearch = !searching && Boolean(emailText.trim() || phoneText.trim() || nameText.trim());

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSearch) return;
    setSearching(true);
    setSearchError(null);
    setExportError(null);
    setExported(false);
    try {
      const identifiers = { emails: splitList(emailText), phones: splitList(phoneText), names: nameText.trim() ? [nameText.trim()] : [] };
      const res = await api.gdpr.search(identifiers);
      setResult(res);
      setSearched(res.identifiers);
      setSelected(new Set(res.matches.filter((m) => m.preselected).map(keyOf)));
    } catch (err) {
      setResult(null);
      setSearched(null);
      setSelected(new Set());
      if (err instanceof ApiError && err.status === 403) setSearchError("Only an admin can search for a person's data, and only from this page.");
      else setSearchError(friendlyApiError(err, "The search could not be completed. Please try again."));
    } finally {
      setSearching(false);
    }
  }

  // Grouped by record type, in the order the server listed them (the inventory's reading order).
  const groups = useMemo(() => {
    const out: { model: string; label: string; matches: GdprPersonMatch[] }[] = [];
    for (const m of result?.matches ?? []) {
      let g = out.find((x) => x.model === m.model);
      if (!g) {
        g = { model: m.model, label: result?.recordTypes[m.model] ?? m.model, matches: [] };
        out.push(g);
      }
      g.matches.push(m);
    }
    return out;
  }, [result]);

  const selectedRefs: GdprRecordRef[] = useMemo(
    () => (result?.matches ?? []).filter((m) => selected.has(keyOf(m))).map((m) => ({ model: m.model, id: m.id })),
    [result, selected],
  );

  function toggle(m: GdprPersonMatch) {
    setExported(false);
    setSelected((prev) => {
      const next = new Set(prev);
      const k = keyOf(m);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  function setGroup(matches: GdprPersonMatch[], on: boolean) {
    setExported(false);
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of matches) {
        if (on) next.add(keyOf(m));
        else next.delete(keyOf(m));
      }
      return next;
    });
  }

  async function doExport() {
    if (!searched || selectedRefs.length === 0) return;
    setExporting(true);
    setExportError(null);
    setExported(false);
    try {
      await api.gdpr.downloadExport(searched, selectedRefs);
      setExported(true);
    } catch (err) {
      setExportError(friendlyApiError(err, "The export could not be made. Please try again."));
    } finally {
      setExporting(false);
    }
  }

  const fieldClass = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green";

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Personal data requests"
        subtitle={
          <span className="block max-w-3xl space-y-2">
          <span className="block">
            Use this page when someone asks for a copy of the personal information we hold about them (a subject access
            request), or asks us to erase it. Search for them by their email address, phone number or name.
          </span>
          <span className="block">
            Records found by their email address or phone number are ticked for you. Records found only by a name, or linked to
            a customer or subcontractor account that may be a company, are left unticked: tick them only once you are sure they
            are this person&apos;s.
          </span>
          <span className="block">
            The export is made when you press the button and is not kept anywhere on the platform. Our accounting records in
            Xero are not included. Only an admin can use this page, and every search and export is recorded in the audit trail.
          </span>
          </span>
        }
      />

      <form onSubmit={submit} className="mb-6 max-w-3xl rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Find a person">
        <h2 className="mb-4 font-heading text-xl text-ele-blue">Find a person</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm" htmlFor="gdpr-email">
              Email address
            </label>
            <input
              id="gdpr-email"
              type="text"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="none"
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm" htmlFor="gdpr-phone">
              Phone number
            </label>
            <input id="gdpr-phone" type="text" inputMode="tel" autoComplete="off" value={phoneText} onChange={(e) => setPhoneText(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label className="mb-1 block text-sm" htmlFor="gdpr-name">
              Name
            </label>
            <input id="gdpr-name" type="text" autoComplete="off" value={nameText} onChange={(e) => setNameText(e.target.value)} className={fieldClass} />
          </div>
        </div>
        <p className="mt-2 text-xs text-ele-body">Each is optional, but give at least one. Separate several email addresses or phone numbers with commas.</p>
        {searchError && (
          <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {searchError}
          </p>
        )}
        <div className="mt-5">
          <Button type="submit" disabled={!canSearch}>
            {searching ? "Searching..." : "Search"}
          </Button>
        </div>
      </form>

      {result && (
        <section aria-label="Records found">
          {result.matches.length === 0 ? (
            <EmptyState message="Nothing on the platform matches these details. Check the spelling, or try another email address or phone number." />
          ) : (
            <>
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-ele-body">
                  {result.matches.length} record{result.matches.length === 1 ? "" : "s"} found, {selected.size} ticked.
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setGroup(result.matches, true)}>
                    Tick all
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setGroup(result.matches, false)}>
                    Untick all
                  </Button>
                </div>
              </div>
              {result.truncated.length > 0 && (
                <p role="status" className="mb-3 rounded-ele border border-amber-400 bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                  More records matched than can be listed for: {result.truncated.join(", ")}. Search with fewer or more exact details to see them all.
                </p>
              )}
              <div className="space-y-4">
                {groups.map((g) => (
                  <div key={g.model} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h3 className="font-heading text-lg text-ele-blue">
                        {g.label} ({g.matches.length})
                      </h3>
                      <div className="flex gap-2 text-xs">
                        <button type="button" onClick={() => setGroup(g.matches, true)} className="underline">
                          Tick these
                        </button>
                        <button type="button" onClick={() => setGroup(g.matches, false)} className="underline">
                          Untick these
                        </button>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {g.matches.map((m) => (
                        <li key={keyOf(m)}>
                          <label className="flex items-start gap-3 text-sm">
                            <input type="checkbox" className="mt-1" checked={selected.has(keyOf(m))} onChange={() => toggle(m)} />
                            <span>
                              <span className="text-ele-strong">{m.label}</span>
                              <span className="ml-2 text-xs uppercase tracking-widest text-ele-blue">{BASIS_LABELS[m.basis]}</span>
                              {!m.preselected && m.note && <span className="mt-0.5 block text-xs text-ele-body">{m.note}</span>}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-6 max-w-3xl rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
                <h2 className="font-heading text-xl text-ele-blue">Subject access export</h2>
                <p className="mt-1 text-sm text-ele-body">
                  One ZIP file: a readable summary, every ticked record in full, a list of the documents on file, and copies of
                  the documents that are small enough to include. Bank details are masked in the summary and given in full only in
                  the record file. Check it before you send it on: a record can also mention other people.
                </p>
                {exportError && (
                  <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                    {exportError}
                  </p>
                )}
                {exported && (
                  <p role="status" className="mt-3 text-sm text-ele-green">
                    The export has been downloaded, and recorded in the audit trail.
                  </p>
                )}
                <Button className="mt-4" onClick={() => void doExport()} disabled={exporting || selectedRefs.length === 0}>
                  {exporting ? "Preparing the export..." : "Download subject access export (ZIP)"}
                </Button>
                {selectedRefs.length === 0 && <p className="mt-2 text-xs text-ele-body">Tick at least one record to export.</p>}
              </div>

              {/* ---- SECOND ACTION PANEL (Stream G, Item 3): erasure, reading this page's one selection: `searched` (the
                  identifiers the records were found by) and `selectedRefs` (the records ticked). */}
              {searched && <ErasurePanel identifiers={searched} records={selectedRefs} />}
            </>
          )}
        </section>
      )}

      <LiftErasureRecord />
    </div>
  );
}
